import type { IPublicClientApplication } from '@azure/msal-browser';
import { apiScopes } from '../auth/msalConfig';

const baseUrl = import.meta.env.VITE_API_BASE_URL ?? '';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
  ) {
    super(message);
  }
}

/** Loop guard: at most 2 automatic re-auth redirects per minute, else surface the real error. */
function shouldRedirect(): boolean {
  try {
    const now = Date.now();
    const raw = sessionStorage.getItem('hlhc.reauth') ?? '[]';
    const recent = (JSON.parse(raw) as number[]).filter((t) => now - t < 60_000);
    if (recent.length >= 2) return false;
    sessionStorage.setItem('hlhc.reauth', JSON.stringify([...recent, now]));
    return true;
  } catch {
    return true;
  }
}

async function acquireToken(msal: IPublicClientApplication): Promise<string> {
  const account = msal.getActiveAccount() ?? msal.getAllAccounts()[0];
  if (!account) throw new ApiError(401, 'Not signed in');
  try {
    const result = await msal.acquireTokenSilent({ scopes: apiScopes, account });
    return result.accessToken;
  } catch (err) {
    // Silent acquisition can fail for many recoverable reasons - expired sessions,
    // Conditional Access sign-in frequency (AADSTS70044), consent - all of which an
    // interactive redirect resolves. Redirect for any of them, with a loop guard so an
    // unsatisfiable policy surfaces its real error instead of looping.
    if (shouldRedirect()) {
      await msal.acquireTokenRedirect({ scopes: apiScopes, account }).catch(() => undefined);
      throw new ApiError(401, 'redirecting_to_signin');
    }
    throw new ApiError(401, `Microsoft sign-in failed: ${(err as Error).message ?? 'unknown error'}`);
  }
}

export function createApi(msal: IPublicClientApplication) {
  async function call<T>(method: string, path: string, body?: unknown): Promise<T> {
    const token = await acquireToken(msal);
    const res = await fetch(`${baseUrl}${path}`, {
      method,
      headers: {
        Authorization: `Bearer ${token}`,
        ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
    if (!res.ok) {
      const payload = (await res.json().catch(() => ({}))) as { error?: string };
      throw new ApiError(res.status, payload.error ?? res.statusText);
    }
    return (await res.json()) as T;
  }

  return {
    get: <T>(path: string) => call<T>('GET', path),
    post: <T>(path: string, body: unknown) => call<T>('POST', path, body),
    put: <T>(path: string, body: unknown) => call<T>('PUT', path, body),
    patch: <T>(path: string, body: unknown) => call<T>('PATCH', path, body),
    delete: <T>(path: string) => call<T>('DELETE', path),
    /** Upload a raw binary body (e.g. an audio file) with its content type. */
    async postRaw<T>(path: string, body: Blob, contentType: string): Promise<T> {
      const token = await acquireToken(msal);
      const res = await fetch(`${baseUrl}${path}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': contentType },
        body,
      });
      if (!res.ok) {
        const payload = (await res.json().catch(() => ({}))) as { error?: string };
        throw new ApiError(res.status, payload.error ?? res.statusText);
      }
      return (await res.json()) as T;
    },
    async blob(path: string): Promise<Blob> {
      const token = await acquireToken(msal);
      const res = await fetch(`${baseUrl}${path}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new ApiError(res.status, res.statusText);
      return res.blob();
    },
    /** Subscribe to call lifecycle events (SSE). Token is re-acquired on reconnect. */
    async events(onEvent: (type: string, data: unknown) => void): Promise<() => void> {
      // EventSource cannot set headers, so we use fetch + streaming body.
      const controller = new AbortController();
      const token = await acquireToken(msal);
      void (async () => {
        const res = await fetch(`${baseUrl}/api/calls/stream`, {
          headers: { Authorization: `Bearer ${token}` },
          signal: controller.signal,
        });
        const reader = res.body?.getReader();
        if (!reader) return;
        const decoder = new TextDecoder();
        let buffer = '';
        for (;;) {
          const { value, done } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          let idx: number;
          while ((idx = buffer.indexOf('\n\n')) >= 0) {
            const chunk = buffer.slice(0, idx);
            buffer = buffer.slice(idx + 2);
            const type = /^event: (.*)$/m.exec(chunk)?.[1];
            const data = /^data: (.*)$/m.exec(chunk)?.[1];
            if (type && data) onEvent(type, JSON.parse(data));
          }
        }
      })().catch(() => undefined);
      return () => controller.abort();
    },
  };
}

export type Api = ReturnType<typeof createApi>;
